import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react';
import { getFactById } from '../../../../../services';

const FactDetailContext = createContext();

const initialState = {
  factDetail: null,
  loading: false,
  error: null,
};

export const FactDetailProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const fetchRequestIdRef = useRef(0);

  const fetchFactDetail = useCallback(
    async (factId) => {
      if (!factId) {
        setState(initialState);
        return;
      }

      const requestId = ++fetchRequestIdRef.current;

      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));

      try {
        const response = await getFactById(factId);

        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        // Verificar si la respuesta contiene un error
        // El servicio getFactById ya maneja errores y retorna datos mock,
        // pero verificamos por si acaso hay algún error residual
        // Verificamos factUuid en lugar de title porque title puede estar vacío
        if (!response || (!response.factUuid && !response.fact_uuid)) {
          throw new Error('Invalid fact data');
        }

        // Transform the response to match the expected format
        // Ahora response viene en camelCase desde getFactById
        const factDetail = {
          factName: response.title || 'Fact',
          factDescription: response.description || '',
          status: response.status || 'ACTIVE',
          createdBy: response.createdBy || '—',
          createdByInitials: response.createdBy
            ? response.createdBy
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
            : null,
          verifiedBy: response.verifiedBy || null,
          verifiedByInitials: response.verifiedBy
            ? response.verifiedBy
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
            : null,
          createdOn: response.createdAt || null,
          verifiedOn: response.verifiedAt || null,
          lastUpdated: response.updatedAt || null,
          expiration: response.expirationPolicy
            ? `${response.expirationPolicy.limit} ${response.expirationPolicy.type}`
            : response.noExpire
            ? 'Never'
            : '—',
          source: response.sourceType || '—',
          sourceDocument: response.sourceDocument || null,
          // Include raw data for any additional fields
          raw: response,
        };

        setState({
          factDetail,
          loading: false,
          error: null,
        });
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        console.error('Error fetching fact detail:', error);
        setState({
          factDetail: null,
          loading: false,
          error,
        });
      }
    },
    []
  );

  const resetFactDetail = useCallback(() => {
    setState(initialState);
    fetchRequestIdRef.current = 0;
  }, []);

  const value = {
    factDetail: state.factDetail,
    loading: state.loading,
    error: state.error,
    fetchFactDetail,
    resetFactDetail,
  };

  return (
    <FactDetailContext.Provider value={value}>
      {children}
    </FactDetailContext.Provider>
  );
};

export const useFactDetailContext = () => {
  const context = useContext(FactDetailContext);
  if (!context) {
    throw new Error('useFactDetailContext must be used within FactDetailProvider');
  }
  return context;
};

